import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { BreathingGradient, SignalDot } from "@/components/ui/living-brand"
import { Text } from "@/components/ui/typography"
import { useReducedMotion } from "@/hooks/useReducedMotion"
import { sfIcons } from "@/lib/sf-icons"
import { cn } from "@/lib/utils"
import { componentTokens, semanticTokens } from "@supabank/config/theme"
import { motion } from "framer-motion"

import { WaitlistForm } from "./WaitlistForm"

type HeroSectionProps = {
  onSubmitSuccess?: () => void
  id?: string
}

// Hero bullet point icons and keys (unified via sfIcons for consistent stroke-2)
const heroBulletConfig = [
  { icon: sfIcons.clock, key: "settlementTime", delay: 0.3 },
  { icon: sfIcons.global, key: "countriesSupported", delay: 0.4 },
  { icon: sfIcons.building2, key: "personalOrBusiness", delay: 0.5 },
] as const

export function HeroSection({ onSubmitSuccess, id }: HeroSectionProps) {
  const { t } = useTranslation("marketing")
  const [submitted, setSubmitted] = useState(false)
  const prefersReducedMotion = useReducedMotion()

  const handleSuccess = () => {
    setSubmitted(true)
    onSubmitSuccess?.()
  }

  // Canonical motion duration: 240ms
  const motionDuration = prefersReducedMotion ? 0.01 : 0.24

  return (
    <section
      id={id}
      className={cn(
        "relative overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        // Mobile First: smaller padding on mobile, progressive enhancement
        "py-16 sm:py-20 md:py-24 lg:py-28 xl:py-32",
        // CANON: Hero gradient background (lavenderGlow for fintech feel)
        semanticTokens.heroGradient.lavenderGlow.tailwind,
        // ENHANCED dark mode: deeper gradients for premium feel with particles
        "dark:bg-black",
        "dark:bg-[radial-gradient(ellipse_at_20%_30%,hsla(270,95%,75%,0.12)_0%,transparent_50%),radial-gradient(ellipse_at_80%_70%,hsla(217,91%,60%,0.10)_0%,transparent_50%),radial-gradient(ellipse_at_50%_50%,hsla(222,70%,55%,0.08)_0%,transparent_60%),hsl(var(--background))]"
      )}
      aria-labelledby="landing-hero-title"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Animated Gradient Strip - cycles through brand gradients */}
      {/* ENHANCED: Stronger colors for intentional design presence (Stripe/Linear style) */}
      <div
        className={cn(
          "absolute inset-x-0 top-0 z-0",
          // Height: elegant strip, not full background
          "h-[65%] sm:h-[60%] md:h-[55%]",
          // Mask: fade out at bottom for smooth blend
          "mask-image-[linear-gradient(to_bottom,black_70%,transparent_100%)]",
          "[mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]",
          "[-webkit-mask-image:linear-gradient(to_bottom,black_70%,transparent_100%)]",
          // Animated gradient - cycles through 4 brand colors
          "animate-gradient-shift",
          // Light theme gradient positions - ENHANCED opacity
          "bg-[length:400%_400%]",
          "bg-gradient-to-br",
          "from-[hsla(270,95%,75%,0.32)] via-[hsla(222,59%,58%,0.24)] to-[hsla(160,84%,39%,0.18)]",
          // Dark theme - ENHANCED for premium dark feel
          "dark:from-[hsla(217,91%,60%,0.28)] dark:via-[hsla(270,80%,65%,0.22)] dark:to-[hsla(222,70%,55%,0.25)]"
        )}
        aria-hidden="true"
      />

      {/* Living Brand: Breathing Gradient - syncs with LED heartbeat */}
      {/* ENHANCED: Increased opacity for visible "living" effect */}
      <BreathingGradient
        variant="lavender"
        syncWithLed
        className="absolute inset-x-0 top-0 h-[45%] z-[1] pointer-events-none opacity-25 dark:opacity-15 [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]"
      />

      {/* Content layer - above gradient backgrounds */}
      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {/* Hero Content - Centered */}
          <div className="text-center space-y-8 sm:space-y-10 lg:space-y-12">
            {/* === HERO TYPOGRAPHY - CANON v1.5.0 Semantic Tokens === */}
            <div className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-7">
              {/* 1. HEADLINE - Hero Scale (Satoshi Black)
                  CANON: hero-xs (36px) → hero-2xl (76px)
                  Line-height: 0.95 (tight for display)
                  Letter-spacing: -0.02em to -0.03em
                  Mobile optimization: Slightly smaller base size for better hierarchy */}
              <motion.h1
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.7, ease: "easeOut" }}
                id="landing-hero-title"
                className={cn(
                  // SSOT: Hero responsive scale from tailwind.config.ts
                  // Mobile-first: Start with slightly smaller size for better hierarchy
                  "font-display font-black",
                  "text-[2rem] leading-[0.95] tracking-[-0.02em]", // 32px base for mobile (better hierarchy)
                  "xs:text-hero-xs sm:text-hero-sm md:text-hero-md lg:text-hero-lg xl:text-hero-xl 2xl:text-hero-2xl",
                  "sm:tracking-[-0.03em]",
                  // TEXT GRADIENT solid colors for visibility - ENHANCED for dark theme
                  "bg-clip-text text-transparent",
                  semanticTokens.textGradient.hero,
                  // Dark mode: MAXIMUM contrast - bright white-blue gradient
                  "dark:bg-clip-text dark:text-transparent",
                  "dark:bg-gradient-to-r dark:from-blue-100 dark:via-white dark:to-indigo-100",
                  // Stronger glow shadow for dark theme readability
                  "dark:drop-shadow-[0_0_20px_rgba(147,197,253,0.4)]",
                  "dark:[text-shadow:0_0_40px_rgba(59,130,246,0.3)]",
                  // Ensure z-index above background
                  "relative z-20",
                )}
              >
                {t("value.hero.headline")}
              </motion.h1>

              {/* 2. SUBHEADLINE - H2
                  Mobile optimization: Better size progression for readability */}
              <motion.h2
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.01 : 0.5,
                  delay: prefersReducedMotion ? 0 : 0.2,
                  ease: "easeOut"
                }}
                className={cn(
                  "mx-auto max-w-3xl",
                  // Mobile: Use heading-lg (24px) for better hierarchy, then scale up
                  "text-heading-lg leading-tight", // 24px base
                  "sm:text-display-sm md:text-display-md lg:text-display-lg", // 30px → 36px → 48px
                  semanticTokens.fg.muted,
                  "font-display font-semibold"
                )}
              >
                {t("value.hero.subheadline")}
              </motion.h2>

              {/* 3. EXPANDING SENTENCE - Body Large
                  Mobile optimization: Slightly larger for better readability */}
              <motion.p
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: prefersReducedMotion ? 0.01 : 0.5,
                  delay: prefersReducedMotion ? 0 : 0.3,
                  ease: "easeOut"
                }}
                className={cn(
                  "mx-auto max-w-2xl",
                  // Mobile: Use body-md (16px) base, then scale to body-lg (18px) on sm+
                  "text-body-md leading-relaxed", // 16px base
                  "sm:text-body-lg", // 18px on sm+
                  semanticTokens.fg.muted
                )}
              >
                {t("value.hero.description")}
              </motion.p>
            </div>

            {/* === CTA FORM SECTION === */}
            <div className="space-y-6 sm:space-y-8">
              {submitted ? (
                <motion.div
                  initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: motionDuration, ease: "easeOut" }}
                  className={cn(
                    "rounded-2xl p-4 sm:p-5 md:p-6 text-center relative overflow-hidden",
                    // Canon v1.4.5: Zen State for success celebration
                    semanticTokens.zen.bg,
                    semanticTokens.zen.border,
                    "border"
                  )}
                >
                  {/* Specular highlight */}
                  <div className={semanticTokens.glass.specular} />
                  <div className="flex items-center justify-center gap-2 relative z-10">
                    <SignalDot signal="pulse" size="sm" color="mint" />
                    <Text variant="sm" className={cn(semanticTokens.zen.fg, "font-medium")}>
                      {t("status.successWaitlist")}
                    </Text>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: motionDuration,
                    delay: prefersReducedMotion ? 0 : 0.4,
                    ease: "easeOut"
                  }}
                  className="space-y-4"
                >
                  <div className="relative rounded-2xl p-1">
                    <WaitlistForm
                      variant="compact"
                      source="landing_hero"
                      submitLabel={t("cta.waitlist")}
                      onSuccess={handleSuccess}
                      autoRedirect={false}
                      inputClassName={cn(
                        componentTokens.input.default,
                        "shadow-none transition-all duration-240"
                      )}
                    />
                  </div>
                </motion.div>
              )}

              {/* === HERO BULLETS - Icon + Text (Below Form) === */}
              <motion.div
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: motionDuration,
                  delay: prefersReducedMotion ? 0 : 0.5,
                  ease: "easeOut"
                }}
                className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-x-8 sm:gap-y-5 pt-4"
              >
                {heroBulletConfig.map((bullet, index) => {
                  const Icon = bullet.icon
                  return (
                    <motion.div
                      key={index}
                      initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: motionDuration,
                        delay: prefersReducedMotion ? 0 : bullet.delay,
                        ease: "easeOut"
                      }}
                      className={cn(
                        "flex items-center gap-2 sm:gap-2.5",
                        // CRISP 3D EFFECT: Remove glassmorphism on dark theme to eliminate "extra layer"
                        "px-4 py-2.5 sm:px-5 sm:py-3",
                        "rounded-xl",
                        // Light theme: glassmorphism for premium feel
                        semanticTokens.glass.surface.combined,
                        "border border-white/18",
                        // Dark theme: Darker, more integrated background that fits dark theme
                        // Use muted background with opacity for better integration
                        "dark:bg-slate-800/40 dark:backdrop-blur-0",
                        "dark:border-slate-700/50",
                        // Dark theme: Subtle shadow for depth without sharp corners
                        "dark:shadow-[0_2px_8px_rgba(0,0,0,0.3)]",
                        // Interactive hover
                        !prefersReducedMotion && "transition-all duration-240 ease-standard",
                        !prefersReducedMotion && "hover:scale-105 hover:shadow-lg",
                        !prefersReducedMotion && "dark:hover:bg-slate-800/60 dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.4)]",
                        // Touch target ≥44px (mobile-first)
                        "min-h-[44px] sm:min-h-0"
                      )}
                    >
                      {/* Icon with gradient accent */}
                      <Icon
                        className={cn(
                          "h-5 w-5 sm:h-6 sm:w-6",
                          "text-primary",
                          "flex-shrink-0"
                        )}
                        aria-hidden="true"
                      />
                      {/* Text */}
                      <Text
                        variant="sm"
                        className={cn(
                          "font-medium",
                          semanticTokens.fg.default,
                          // Dark theme: Lighter text color for better contrast on dark background
                          "dark:text-slate-200",
                          // Mobile: allow wrapping for long text, desktop: nowrap
                          "whitespace-normal sm:whitespace-nowrap"
                        )}
                      >
                        {t(`value.hero.bullets.${bullet.key}`)}
                      </Text>
                    </motion.div>
                  )
                })}
              </motion.div>

              {/* Trust Signals REMOVED - information already shown in bullet points above */}
            </div>
          </div>
        </div>
    </section>
  )
}
